import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import ProgressBar from "@/components/ProgressBar";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import neilLogo from "@/assets/neil-ai-logo-new.png";
import puduLogo from "@/assets/pudu-logo-official.png";
import {
  COMPANY_LINKEDIN_URL,
  ORGANIZATION_NAME,
  ORGANIZATION_URL,
  TEAM_BOOKING_URL,
  TEAM_CALL_DISPLAY,
  TEAM_CALL_URL,
  getTeamMemberBySlug,
} from "@/lib/teamData";
import { ArrowLeft, Award, BriefcaseBusiness, CalendarDays, Linkedin, MapPin, PhoneCall, ShieldCheck, Sparkles } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import NotFound from "./NotFound";

const TeamMemberProfile = () => {
  const { slug } = useParams();
  const member = getTeamMemberBySlug(slug);

  if (!member) {
    return <NotFound />;
  }

  const canonical = `${ORGANIZATION_URL}/team/${member.slug}`;
  const title = `${member.name} | ${member.title} | Neil AI Global Solutions`;
  const description = `${member.name} is ${member.title} at Neil AI Global Solutions. Learn about expertise, certifications, industries served, and how to connect with the team.`;

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${ORGANIZATION_URL}/#organization`,
        name: ORGANIZATION_NAME,
        url: ORGANIZATION_URL,
        sameAs: [COMPANY_LINKEDIN_URL],
      },
      {
        "@type": ["Person", "Employee"],
        "@id": `${canonical}#person`,
        name: member.name,
        jobTitle: member.title,
        description: member.fullBio,
        image: `${ORGANIZATION_URL}${member.image}`,
        url: canonical,
        sameAs: [member.linkedin],
        knowsAbout: member.expertise,
        worksFor: {
          "@id": `${ORGANIZATION_URL}/#organization`,
        },
        homeLocation: {
          "@type": "Place",
          name: member.areasServed.join(", "),
        },
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: title,
        description,
        about: {
          "@id": `${canonical}#person`,
        },
      },
    ],
  };

  return (
    <>
      <SEO title={title} description={description} canonical={canonical} keywords={`${member.name}, ${member.title}, Neil AI team, AI automation leadership, robotics leadership`} />
      <StructuredData id={`team-member-${member.slug}`} data={structuredData} />
      <ProgressBar />
      <Navigation />

      <div
        className="min-h-screen px-4 pb-8 pt-40 sm:pt-44 md:pt-48"
        style={{
          background:
            "radial-gradient(1400px 900px at 15% 5%, hsl(var(--primary) / 0.15) 0%, transparent 55%), radial-gradient(1100px 700px at 85% 75%, hsl(var(--accent) / 0.12) 0%, transparent 55%), linear-gradient(180deg, hsl(210 83% 8%) 0%, hsl(210 83% 4%) 100%)",
        }}
      >
        <main
          className="mx-auto max-w-6xl overflow-hidden rounded-[28px] border border-[hsl(var(--line))] p-4 sm:p-6 md:p-8"
          style={{
            boxShadow: "0 0 80px hsl(var(--primary) / 0.2), 0 12px 42px rgba(0, 0, 0, 0.56)",
            backgroundImage:
              "radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.16), transparent 30%), linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--background)) 100%)",
          }}
        >
          <Link to="/#team" className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
            <ArrowLeft className="h-4 w-4" />
            Back to team section
          </Link>

          <section className="grid gap-8 lg:grid-cols-[340px_1fr] lg:gap-10">
            <div className="relative">
              <div className="relative mx-auto aspect-square max-w-[320px] rounded-full">
                <span
                  className="absolute inset-0 rounded-full"
                  style={{ boxShadow: "0 0 36px hsl(var(--primary) / 0.72), inset 0 0 22px hsl(var(--primary) / 0.3)" }}
                />
                <span className="absolute inset-[12px] rounded-full border-[6px] border-[hsl(var(--accent))]" />
                <span className="absolute inset-[20px] overflow-hidden rounded-full border border-white/10 bg-background/70">
                  <img src={member.image} alt={`${member.name} headshot`} className="h-full w-full object-cover object-top" />
                </span>
              </div>

              <div className="mt-6 rounded-[24px] border border-[hsl(var(--line))] bg-background/60 p-5 backdrop-blur-sm">
                <div className="flex items-center justify-center gap-3 border-b border-[hsl(var(--line))] pb-4">
                  <img src={neilLogo} alt="Neil AI Global Solutions" className="h-14 w-auto object-contain" />
                </div>
                <div className="mt-4 space-y-3 text-sm text-foreground">
                  <div className="flex items-start gap-3">
                    <Award className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{member.certifications[0]}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Authorized PUDU Robotics support network</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{member.areasServed.join(", ")}</span>
                  </div>
                </div>
                <div className="mt-5 flex flex-col gap-3">
                  <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <a href={member.contactUrl} target="_blank" rel="noopener noreferrer">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      Contact {member.name.split(" ")[0]}
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="rounded-full border-accent/50 bg-background/50 text-accent hover:bg-accent/10 hover:text-accent">
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                      <Linkedin className="mr-2 h-4 w-4" />
                      LinkedIn
                    </a>
                  </Button>
                </div>
                <div className="mt-5 flex items-center justify-center gap-3 border-t border-[hsl(var(--line))] pt-4">
                  <img src={puduLogo} alt="PUDU authorized dealer" className="h-8 w-auto object-contain" />
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">Authorized Dealer</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-bold tracking-[0.32em] text-primary">{member.category.toUpperCase()}</p>
              <h1 className="mt-3 font-orbitron text-3xl font-bold leading-tight text-foreground md:text-5xl">
                {member.name}
              </h1>
              <p className="mt-4 text-xl font-semibold uppercase tracking-[0.06em] text-accent md:text-2xl">{member.title}</p>
              <p className="mt-2 text-sm font-medium uppercase tracking-[0.08em] text-foreground/90 md:text-base">{member.subtitle}</p>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">{member.fullBio}</p>

              <div className="mt-8 grid gap-5 md:grid-cols-2">
                <div className="rounded-[24px] border border-[hsl(var(--line))] bg-background/55 p-5 backdrop-blur-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h2 className="font-orbitron text-xl font-bold text-foreground">Expertise</h2>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {member.expertise.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-[24px] border border-[hsl(var(--line))] bg-background/55 p-5 backdrop-blur-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <BriefcaseBusiness className="h-5 w-5 text-accent" />
                    <h2 className="font-orbitron text-xl font-bold text-foreground">Industry Experience</h2>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {member.industryExperience.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-[24px] border border-[hsl(var(--line))] bg-background/55 p-5 backdrop-blur-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <Award className="h-5 w-5 text-accent" />
                    <h2 className="font-orbitron text-xl font-bold text-foreground">Certifications</h2>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {member.certifications.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-[24px] border border-[hsl(var(--line))] bg-background/55 p-5 backdrop-blur-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-primary" />
                    <h2 className="font-orbitron text-xl font-bold text-foreground">Areas Served</h2>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {member.areasServed.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 rounded-[24px] border border-[hsl(var(--line))] bg-card/60 p-6 text-center backdrop-blur-sm">
                <h2 className="font-orbitron text-2xl font-bold text-foreground">Connect With Neil AI Leadership</h2>
                <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
                  Talk with our team about AI automation, robotics deployment, pricing, and what the right rollout could look like for your business.
                </p>
                <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Button asChild size="lg" className="rounded-full bg-primary px-8 text-primary-foreground hover:bg-primary/90">
                    <a href={TEAM_BOOKING_URL} target="_blank" rel="noopener noreferrer">BOOK A DEMO</a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="rounded-full border-accent/50 bg-background/50 px-8 text-accent hover:bg-accent/10 hover:text-accent">
                    <a href={TEAM_CALL_URL}>
                      <PhoneCall className="mr-2 h-4 w-4" />
                      CALL {TEAM_CALL_DISPLAY}
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </main>
      </div>
    </>
  );
};

export default TeamMemberProfile;
